import React, {
    useCallback,
    useContext,
    useEffect,
    useState
} from "react";
import axios from "axios";
import {
    Alert
} from "react-bootstrap";
import PropTypes from "prop-types";

import ShopsContext from "../../../context/ShopsContext";
import Loading from "../../../components/Loading";
import DiscountCodeForm from "../components/DiscountCodeForm";
import isNullOrUndefined from "../../../utilities/isNullOrUndefined";

function DiscountCodeDetailEdit({ discountCode, setDiscountCode }) {
    const shopsContext = useContext(ShopsContext);
    const [formState, setFormState] = useState(null);
    const [error, setError] = useState(null);
    const [saving, setSaving] = useState(false);
    const [success, setSuccess] = useState(false);

    useEffect(() => {
        if(discountCode) {
            setFormState({
                ...discountCode,
                enableDiscountValue: !isNullOrUndefined(discountCode.discountValue),
                enableDiscountPercentage: !isNullOrUndefined(discountCode.discountPercentage),
                enableMaxUsageCount: !isNullOrUndefined(discountCode.maxUsageValue),
                maxUsageCount: !isNullOrUndefined(discountCode.maxUsageValue) ? discountCode.maxUsageValue : 1,
                enableStartDate: !isNullOrUndefined(discountCode.startDate),
                enableExpirationDate: !isNullOrUndefined(discountCode.expirationDate),
            });
        }
    }, [discountCode]);

    const onChange = useCallback((newState) => {
        setFormState(prev => ({ ...prev, ...newState }));
    }, []);

    // eslint-disable-next-line complexity
    const updateDiscountCode = useCallback(() => {
        setSuccess(false);
        if(formState.code.trim().length === 0) {
            setError("Code is vereist.");
            window.scroll({ top: 0, behavior: "smooth" });
            return;
        }

        // Discount validation: either value or percentage
        if(formState.enableDiscountValue && (!formState.discountValue || parseFloat(formState.discountValue) <= 0)) {
            setError("Vul een geldig kortingsbedrag in.");
            window.scroll({ top: 0, behavior: "smooth" });
            return;
        }
        if(!formState.enableDiscountValue && (!formState.discountPercentage || parseFloat(formState.discountPercentage) <= 0)) {
            setError("Vul een geldig kortingspercentage in.");
            window.scroll({ top: 0, behavior: "smooth" });
            return;
        }

        setError(null);
        setSaving(true);

        const discountValue = formState.enableDiscountValue ? parseFloat(formState.discountValue) : "null";
        const discountPercentage = !formState.enableDiscountValue ? parseFloat(formState.discountPercentage) : "null";

        axios.post("/updateDiscountCode", {
            shopId: shopsContext.currentShop.id,
            discountCodeId: discountCode.id,
            code: formState.code.trim(),
            type: formState.type,
            discountValue,
            discountPercentage,
            maxUsageCount: formState.enableMaxUsageCount ? parseInt(formState.maxUsageCount, 10) : "null",
            startDate: formState.enableStartDate ? formState.startDate : "",
            expirationDate: formState.enableExpirationDate ? formState.expirationDate : "",
            allProductsEligible: formState.allProductsEligible ? 1 : 0,
            eligibleProductIds: !formState.allProductsEligible && formState.eligibleProducts
                ? JSON.stringify(formState.eligibleProducts.map(product => product.id))
                : JSON.stringify([]),
            requiredProductsAll: formState.requiredProductsAll !== null && formState.requiredProductsAll !== undefined
                ? (formState.requiredProductsAll ? 1 : 0)
                : "null",
            requiredProductIds: formState.requiredProductsAll !== null && formState.requiredProductsAll !== undefined && formState.requiredProducts
                ? JSON.stringify(formState.requiredProducts.map(product => product.id))
                : JSON.stringify([]),
            combinable: formState.combinable ? 1 : 0,
            enabled: formState.enabled ? 1 : 0,
        })
            .then((response) => {
                if(response.data.valid) {
                    setFormState(prev => ({ ...prev, ...response.data.discountCode }));
                    setDiscountCode(response.data.discountCode);
                    setSuccess(true);
                } else {
                    setError("Er ging iets fout. Probeer het later opnieuw. (" + response.data.error + ")");
                }
            })
            .catch(() => {
                setError("Er ging iets fout. Probeer het later opnieuw.");
            })
            .finally(() => {
                window.scroll({ top: 0, behavior: "smooth" });
                setSaving(false);
            });
    }, [formState, discountCode, shopsContext.currentShop.id, setDiscountCode]);

    if(formState === null) {
        return (
            <Loading/>
        )
    }

    return (
        <React.Fragment>
            { error ? (
                <Alert variant="danger">{ error }</Alert>
            ) : success && (
                <Alert variant="success">
                    Wijzigingen opgeslagen!
                </Alert>
            )}
            <DiscountCodeForm
                state={ formState }
                originalState={ discountCode }
                onChange={ onChange }
                loading={ saving }
                onSave={ updateDiscountCode }
                formType="edit"
            />
        </React.Fragment>
    )
}
DiscountCodeDetailEdit.propTypes = {
    discountCode: PropTypes.object,
    setDiscountCode: PropTypes.func.isRequired
}

export default React.memo(DiscountCodeDetailEdit);
